import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Fingerprint, Loader2, Lock, Mail } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleIcon from "@/components/GoogleIcon";
import MicrosoftIcon from "@/components/MicrosoftIcon";

const BRAND = {
  google: { label: "Google", Icon: GoogleIcon },
  microsoft: { label: "Microsoft", Icon: MicrosoftIcon },
  oidc: { label: "organisation sign-in", Icon: Fingerprint },
};

function methodLabel(method) {
  if (method.kind === "password") return method.label || "Email and password";
  const brand = BRAND[method.brand]?.label || method.label || "organisation sign-in";
  const label = method.label || brand;
  return /^continue with\b/i.test(label) ? label : `Continue with ${label}`;
}

function getErrorMessage(error) {
  const code = String(error?.code || error?.data?.code || "").toLowerCase();
  if (code === "provider_not_found" || code === "oauth_provider_not_found") {
    return "That sign-in method is not available. Choose another method or use your email and password.";
  }
  if (code === "authentication_method_required") {
    return "This invitation requires its organisation sign-in method.";
  }
  if (code === "invitation_provider_mismatch") {
    return "This invitation requires a different organisation sign-in method. Start again from the invitation link.";
  }
  if (code === "invitation_identity_mismatch") {
    return "Sign in with the account that received this invitation.";
  }
  if (error instanceof TypeError || /failed to fetch|network|load failed/i.test(String(error?.message || ""))) {
    return "We couldn't start sign-in. Please try again, or choose another available method.";
  }
  return error?.message || error?.data?.error || "We couldn't start sign-in. Please try again.";
}

/**
 * Server-directed sign-in UI shared by the generic login and invitation pages.
 * The component deliberately renders only methods returned by the options API.
 */
export default function SignInForm({
  selector = {},
  returnTo = "/",
  invitation = false,
  onAuthenticated,
  onRegistered,
  initialEmail = "",
}) {
  const uid = useId().replace(/:/g, "");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [mode, setMode] = useState("signin");
  const [methods, setMethods] = useState(null);
  const [emailDelivery, setEmailDelivery] = useState("enabled");
  const [loadingOptions, setLoadingOptions] = useState(Boolean(Object.keys(selector).length));
  const [optionsError, setOptionsError] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeProvider, setActiveProvider] = useState("");
  const requestVersion = useRef(0);

  const loadOptions = useCallback(async (nextSelector, signal) => {
    const version = ++requestVersion.current;
    setLoadingOptions(true);
    setOptionsError("");
    try {
      const result = await base44.auth.getSignInOptions(nextSelector, { signal });
      if (signal?.aborted || version !== requestVersion.current) return;
      setMethods(Array.isArray(result?.methods) ? result.methods : []);
      setEmailDelivery(result?.email_delivery || "enabled");
    } catch (error) {
      if (signal?.aborted || version !== requestVersion.current) return;
      setMethods(null);
      setOptionsError(getErrorMessage(error));
    } finally {
      if (!signal?.aborted && version === requestVersion.current) setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!Object.keys(selector).length) return undefined;
    const controller = new AbortController();
    loadOptions(selector, controller.signal);
    return () => controller.abort();
  // Selectors are commonly supplied as an inline object by a route page. Depend
  // on their values so a render caused by an error/loading state cannot restart
  // the same request and overwrite a newer response.
  }, [loadOptions, selector.email, selector.organisation_slug, selector.invitation_id]);

  const submitEmail = async (event) => {
    event.preventDefault();
    const value = email.trim();
    if (!value) return;
    await loadOptions({ email: value });
  };

  const changeEmail = () => {
    requestVersion.current += 1;
    setMethods(null);
    setFormError("");
    setOptionsError("");
    setPassword("");
  };

  const startProvider = async (method) => {
    setFormError("");
    setActiveProvider(method.provider_id || method.brand || method.kind);
    try {
      const callbackURL = `${window.location.origin}${returnTo}`;
      if (method.kind === "social") {
        // Generic configured social providers are deliberately started through
        // Better Auth. The discovery endpoint lists them, while the
        // server-directed start endpoint is reserved for organisation SSO.
        const result = await base44.auth.loginWithProvider(method.provider_id, {
          callbackURL,
          errorCallbackURL: `${window.location.origin}/login/error`,
          newUserCallbackURL: callbackURL,
          requestSignUp: invitation,
        });
        if (result?.url && result.redirect === true) window.location.assign(result.url);
      } else {
        const result = await base44.auth.startSignIn(selectorForRequest(selector, email), method.provider_id, returnTo);
        if (!result?.url || result.redirect !== true) throw new Error("The sign-in provider did not return a redirect.");
        window.location.assign(result.url);
      }
    } catch (error) {
      const providerName = methodLabel(method).replace(/^Continue with\s+/i, "");
      setFormError(error instanceof TypeError || /failed to fetch|network|load failed/i.test(String(error?.message || ""))
        ? `We couldn't start ${providerName} sign-in. Please try again, or choose another available method.`
        : getErrorMessage(error));
      setActiveProvider("");
    }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      let result;
      if (mode === "create") {
        result = await base44.auth.register({ email: email.trim(), password, full_name: fullName, callbackURL: invitation ? `${window.location.origin}${returnTo}` : undefined });
        const handled = await onRegistered?.(result, { emailDelivery });
        if (handled !== true && !result?.token) {
          setFormError(emailDelivery === "disabled"
            ? "Your account and invitation were saved, but email delivery is disabled. Contact an administrator to finish verification."
            : "Your account was created. Verification email requested; check your inbox, then sign in to continue.");
        }
      } else {
        result = await base44.auth.loginViaEmailPassword(email.trim(), password);
        await onAuthenticated?.(result);
      }
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const passwordMethod = methods?.find((method) => method.kind === "password");
  const providerMethods = (methods || []).filter((method) => method.kind !== "password");
  const hasSelectorContext = Object.keys(selector).length > 0;
  const needsEmail = Boolean(passwordMethod && (hasSelectorContext || !email.trim()));

  return (
    <div className="space-y-4" aria-busy={loadingOptions || submitting || Boolean(activeProvider)}>
      {(optionsError || formError) && <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-800 text-sm">{optionsError || formError}</div>}

      {loadingOptions && <p role="status" className="text-sm text-muted-foreground text-center">Checking available sign-in methods…</p>}

      {!methods && !loadingOptions && !hasSelectorContext && (
        <form onSubmit={submitEmail} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${uid}-email`}>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input id={`${uid}-email`} type="email" autoComplete="email" autoFocus={!hasSelectorContext} placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} className="pl-10 h-12" required />
            </div>
          </div>
          <Button type="submit" className="w-full h-12 font-medium" disabled={!email.trim() || loadingOptions}>
            Continue <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Button>
        </form>
      )}

      {!methods && !loadingOptions && hasSelectorContext && optionsError && (
        <Button type="button" variant="outline" className="w-full h-12" onClick={() => loadOptions(selector)}>
          Try again
        </Button>
      )}

      {methods && (
        <>
          {email && <p className="text-sm text-muted-foreground">Signing in as <strong className="text-foreground">{email}</strong></p>}
          {providerMethods.map((method) => {
            const brand = BRAND[method.brand];
            const Icon = brand?.Icon || Fingerprint;
            const busy = activeProvider === (method.provider_id || method.brand || method.kind);
            return (
              <Button key={`${method.kind}:${method.provider_id || method.brand || method.label}`} type="button" variant="outline" className="w-full h-12 text-sm font-medium" onClick={() => startProvider(method)} disabled={Boolean(activeProvider) || loadingOptions} aria-busy={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Icon className="w-5 h-5" aria-hidden="true" />}
                {busy ? "Continuing…" : methodLabel(method)}
              </Button>
            );
          })}

          {passwordMethod && (
            <>
              {providerMethods.length > 0 && <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-3 text-muted-foreground">or</span></div></div>}
              {invitation && <div role="group" aria-label="Sign in or create an account" className="flex gap-2"><button type="button" aria-pressed={mode === "signin"} onClick={() => { setMode("signin"); setFormError(""); }} className={`flex-1 h-9 rounded-md text-sm font-medium border ${mode === "signin" ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-input"}`}>I have an account</button><button type="button" aria-pressed={mode === "create"} onClick={() => { setMode("create"); setFormError(""); }} className={`flex-1 h-9 rounded-md text-sm font-medium border ${mode === "create" ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-input"}`}>I'm new</button></div>}
              <form onSubmit={submitPassword} className="space-y-4">
                {mode === "create" && <div className="space-y-2"><Label htmlFor={`${uid}-name`}>Full name</Label><Input id={`${uid}-name`} value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" required /></div>}
                {needsEmail && <div className="space-y-2"><Label htmlFor={`${uid}-password-email`}>Email</Label><Input id={`${uid}-password-email`} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></div>}
                <div className="space-y-2"><div className="flex items-center justify-between"><Label htmlFor={`${uid}-password`}>Password</Label>{!invitation && <a href="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</a>}</div><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" /><Input id={`${uid}-password`} type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} className="pl-10 h-12" required /></div></div>
                <Button type="submit" className="w-full h-12 font-medium" disabled={submitting || !email.trim()}>{submitting ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />{mode === "create" ? "Creating account…" : "Signing in…"}</> : mode === "create" ? "Create account with a password" : "Sign in with email and password"}</Button>
              </form>
            </>
          )}

          {email && !hasSelectorContext && <Button type="button" variant="ghost" className="w-full" onClick={changeEmail}><ArrowLeft className="w-4 h-4" aria-hidden="true" />Change email</Button>}
          {emailDelivery === "disabled" && mode === "create" && <p className="text-sm text-muted-foreground" role="status">Your account and invitation will be saved, but email delivery is disabled. You will need to verify or sign in through the available method.</p>}
        </>
      )}
    </div>
  );
}

function selectorForRequest(selector, email) {
  if (selector.invitation_id) return { invitation_id: selector.invitation_id };
  if (selector.organisation_slug) return { organisation_slug: selector.organisation_slug };
  return email ? { email: email.trim() } : {};
}

export { getErrorMessage, methodLabel };
